/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checks, Plus } from '@saleshandy/icons';
import produce from 'immer';
import Input from '../../design-system/components/input';
import Select from '../../design-system/components/select';
import Button from '../../design-system/components/atoms/button';
import Checkbox from '../../design-system/components/atoms/checkbox';
import Icon from '../../design-system/components/atoms/icon/icon';
import TimezoneOptionRenderer from '../../design-system/components/atoms/timezone-option-renderer';
import {
  OverlayTooltip,
  Placement,
} from '../../design-system/components/overlay';
import { getTimezoneValueByName, Timezone } from '../../utils';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import hasPermission from '../../utils/access-control/has-permission';
import { Permissions } from '../../utils/access-control/enums/permissions';
import toaster from '../../toaster';
import { UpdateScheduleRequestPayload } from '../../../components/settings/types/request-payload';
import { Schedule } from '../../../components/settings/types/schedule';
import {
  ModiFyTimeSlot,
  ModifySlot,
  SCHEDULE_DAY_ROWS,
  ScheduleFormErrors,
  ScheduleFormMode,
  ScheduleFormValues,
} from './types';
import ScheduleTimeSlotPill from './schedule-time-slot-pill';
import ScheduleValidationBanner from './schedule-validation-banner';
import ScheduleUsageWarningBanner, {
  ScheduleUsageSequence,
} from './schedule-usage-warning-banner';
import {
  applySlotsToAllActiveDays,
  createCreateModeTimeSlots,
  createDefaultSlot,
  createEmptySlot,
  generateDefaultScheduleName,
  getActiveTimeSlots,
  hasSlotValidationErrors,
  isDayActive,
  normalizeTimeSlotsForCompare,
  parseScheduleTimeSlots,
  shouldShowApplyToActiveDays,
  stripSlotsForPayload,
} from './utils';
import { isSlotTimeFilled } from './time-slot-picker-utils';
import {
  getFirstValidationBannerMessage,
  markSlotValidationErrors,
  validateField,
  validateOverlapTimeSlot,
} from './validator';

type TimezoneOption = Timezone & { key: string };

const MAX_SLOTS_PER_DAY = 3;

export type ScheduleFormProps = {
  mode: ScheduleFormMode;
  schedule?: Schedule;
  existingScheduleNames: string[];
  timezoneList: Timezone[];
  defaultTimezone?: string;
  onSubmit: (payload: UpdateScheduleRequestPayload) => void;
  isSubmitLoading?: boolean;
  submitLabel?: string;
  showInlineFooter?: boolean;
  onDelete?: (schedule: Schedule) => void;
  disabled?: boolean;
  formId?: string;
  onFormValidityChange?: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  showUsageWarning?: boolean;
  usageSequences?: ScheduleUsageSequence[];
  currentSequenceId?: number;
};

const getInitialValues = (
  mode: ScheduleFormMode,
  schedule: Schedule | undefined,
  defaultTimezone: string,
  existingScheduleNames: string[],
): ScheduleFormValues => ({
  scheduleName:
    mode === 'edit' && schedule
      ? schedule.name
      : generateDefaultScheduleName(existingScheduleNames),
  timezone: mode === 'edit' && schedule ? schedule.timezone : defaultTimezone,
});

const getInitialTimeSlots = (
  mode: ScheduleFormMode,
  schedule: Schedule | undefined,
): ModiFyTimeSlot[] => {
  if (mode === 'edit' && schedule?.timeSlots) {
    return parseScheduleTimeSlots(
      typeof schedule.timeSlots === 'string'
        ? schedule.timeSlots
        : JSON.stringify(schedule.timeSlots),
    );
  }
  return createCreateModeTimeSlots();
};

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  mode,
  schedule,
  existingScheduleNames,
  timezoneList,
  defaultTimezone = '',
  onSubmit,
  isSubmitLoading = false,
  submitLabel = 'Save',
  showInlineFooter = true,
  onDelete,
  disabled = false,
  formId = 'schedule-form',
  onFormValidityChange,
  onDirtyChange,
  showUsageWarning = false,
}) => {
  const canEdit = !disabled && hasPermission(Permissions.SCHEDULE_UPDATE);

  const initialSnapshotRef = useRef({
    values: getInitialValues(
      mode,
      schedule,
      defaultTimezone,
      existingScheduleNames,
    ),
    timeSlots: getInitialTimeSlots(mode, schedule),
  });

  const [values, setValues] = useState<ScheduleFormValues>(() =>
    getInitialValues(mode, schedule, defaultTimezone, existingScheduleNames),
  );
  const [errors, setErrors] = useState<ScheduleFormErrors>({
    scheduleName: '',
    timezone: '',
  });
  const [dirty, setDirty] = useState({ scheduleName: false, timezone: false });
  const [timeSlots, setTimeSlots] = useState<ModiFyTimeSlot[]>(() =>
    getInitialTimeSlots(mode, schedule),
  );

  const [slotErrorBanner, setSlotErrorBanner] = useState('');
  const [lastFocusedDay, setLastFocusedDay] = useState<number | null>(null);
  const [focusNewSlot, setFocusNewSlot] = useState<{
    day: number;
    slotIndex: number;
  } | null>(null);

  const validationOptions = useMemo(
    () => ({
      existingScheduleNames,
      currentScheduleName: mode === 'edit' ? schedule?.name : undefined,
    }),
    [existingScheduleNames, mode, schedule?.name],
  );

  const timezoneOptions: TimezoneOption[] = useMemo(
    () =>
      timezoneList.map((timezone) => ({
        key: timezone.name,
        ...timezone,
      })),
    [timezoneList],
  );

  const hasActiveDays = getActiveTimeSlots(timeSlots).length > 0;

  const isFormValid = useMemo(() => {
    if (!hasActiveDays) {
      return false;
    }
    if (errors.scheduleName || errors.timezone) {
      return false;
    }
    if (!values.scheduleName?.trim() || !values.timezone) {
      return false;
    }
    if (hasSlotValidationErrors(timeSlots)) {
      return false;
    }
    return true;
  }, [hasActiveDays, errors, values, timeSlots]);

  useEffect(() => {
    onFormValidityChange?.(isFormValid && !isSubmitLoading);
  }, [isFormValid, isSubmitLoading, onFormValidityChange]);

  const isDirty = useMemo(() => {
    const initial = initialSnapshotRef.current;
    return (
      JSON.stringify(values) !== JSON.stringify(initial.values) ||
      normalizeTimeSlotsForCompare(timeSlots) !==
        normalizeTimeSlotsForCompare(initial.timeSlots)
    );
  }, [values, timeSlots]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Global check: if ANY active day has an incomplete last slot, disable all "Add slot" buttons
  const hasAnyIncompleteLastSlot = useMemo(() => {
    const activeDays = getActiveTimeSlots(timeSlots);
    return activeDays.some((dayTimeSlot) => {
      const { slots } = dayTimeSlot;
      if (slots.length === 0) return false;

      const lastSlot = slots[slots.length - 1];
      return (
        !isSlotTimeFilled(lastSlot.start) || !isSlotTimeFilled(lastSlot.end)
      );
    });
  }, [timeSlots]);

  const validateName = useCallback(
    (name: string) => validateField('scheduleName', name, validationOptions),
    [validationOptions],
  );

  const onInputChange = (
    value: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setDirty((prev) => ({ ...prev, [name]: true }));
    if (name === 'scheduleName') {
      setErrors((prev) => ({
        ...prev,
        scheduleName: validateName(value),
      }));
    }
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (dirty[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, values[name], validationOptions),
      }));
    }
  };

  const onTimezoneSelect = (option: TimezoneOption) => {
    setValues((prev) => ({ ...prev, timezone: option.key }));
    setDirty((prev) => ({ ...prev, timezone: true }));
    setErrors((prev) => ({
      ...prev,
      timezone: validateField('timezone', option.key, validationOptions),
    }));
  };

  const clearSlotErrorBanner = () => {
    setSlotErrorBanner('');
  };

  const setFocusedDay = (day: number) => {
    setLastFocusedDay(day);
  };

  const onDayToggle = (day: number, checked: boolean) => {
    if (!canEdit) {
      return;
    }
    setFocusedDay(day);
    setTimeSlots((prev) => {
      const next = prev.map((timeSlot) => {
        if (timeSlot.day !== day) {
          return timeSlot;
        }
        if (checked) {
          return { ...timeSlot, slots: [createDefaultSlot(0)] };
        }
        return { ...timeSlot, slots: [] };
      });
      const bannerMessage = getFirstValidationBannerMessage(next);
      setSlotErrorBanner(bannerMessage || '');
      return next;
    });
  };

  const onSlotChange = (day: number, slotIndex: number, slot: ModifySlot) => {
    clearSlotErrorBanner();
    setFocusedDay(day);
    setTimeSlots((prev) =>
      produce(prev, (draft) => {
        const timeSlot = draft.find((t) => t.day === day);
        if (timeSlot) {
          timeSlot.slots[slotIndex] = slot;
        }
      }),
    );
  };

  const onInputSlotBlur = () => {
    setTimeSlots((prev) => {
      const { timeSlots: validatedSlots } = markSlotValidationErrors(prev);
      const bannerMessage = getFirstValidationBannerMessage(validatedSlots);
      setSlotErrorBanner(bannerMessage || '');
      return validatedSlots;
    });
  };

  const addSlotHandler = (day: number) => {
    const timeSlot = timeSlots.find((t) => t.day === day);
    if (!timeSlot || !isDayActive(timeSlot)) {
      return;
    }
    if (timeSlot.slots.length >= MAX_SLOTS_PER_DAY) {
      toaster.error('More than 3 slots are not allowed for a single day');
      return;
    }
    clearSlotErrorBanner();
    setFocusedDay(day);
    const newSlotIndex = timeSlot.slots.length;
    setFocusNewSlot({ day, slotIndex: newSlotIndex });
    setTimeSlots((prev) =>
      produce(prev, (draft) => {
        const slot = draft.find((t) => t.day === day);
        if (slot) {
          slot.slots.push(createEmptySlot(slot.slots.length));
        }
      }),
    );
  };

  const removeSlot = (day: number, slotIndex: number) => {
    setFocusedDay(day);
    setTimeSlots((prev) => {
      const next = produce(prev, (draft) => {
        const timeSlot = draft.find((t) => t.day === day);
        if (!timeSlot) {
          return;
        }
        timeSlot.slots = timeSlot.slots.filter(
          (_, index) => index !== slotIndex,
        );
        if (timeSlot.slots.length === 0) {
          return;
        }
        const slotIds = validateOverlapTimeSlot(timeSlot);
        timeSlot.slots = timeSlot.slots.map((s, index) => ({
          ...s,
          id: index,
          overlapError: slotIds.includes(s.id),
        }));
      });
      const { timeSlots: validatedSlots } = markSlotValidationErrors(next);
      const bannerMessage = getFirstValidationBannerMessage(validatedSlots);
      setSlotErrorBanner(bannerMessage || '');
      return validatedSlots;
    });
  };

  const onApplyToActiveDays = (sourceDay: number) => {
    setTimeSlots((prev) => applySlotsToAllActiveDays(prev, sourceDay));
    setSlotErrorBanner('');
    setLastFocusedDay(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) {
      return;
    }

    const nameError = validateName(values.scheduleName);
    const timezoneError = validateField(
      'timezone',
      values.timezone,
      validationOptions,
    );

    let isError = !!(nameError || timezoneError);

    const {
      timeSlots: validatedSlots,
      hasError: slotHasError,
    } = markSlotValidationErrors(timeSlots);
    isError = isError || slotHasError;

    const bannerMessage = getFirstValidationBannerMessage(validatedSlots);
    setSlotErrorBanner(bannerMessage || '');

    setErrors({
      scheduleName: nameError,
      timezone: timezoneError,
    });
    setTimeSlots(validatedSlots);
    setDirty({ scheduleName: true, timezone: true });

    if (isError || !hasActiveDays) {
      return;
    }

    onSubmit({
      name: values.scheduleName.trim(),
      timezone: values.timezone,
      timeSlots: stripSlotsForPayload(validatedSlots),
    });
  };

  return (
    <form
      id={formId}
      className="bs-schedule-form schedule-form"
      onSubmit={handleSubmit}
    >
      {showUsageWarning && schedule?.scheduleSequenceCount > 1 && (
        <ScheduleUsageWarningBanner
          otherSequenceCount={schedule?.scheduleSequenceCount - 1 || 0}
        />
      )}

      <div className="bs-schedule-form__field">
        <label>Schedule Name</label>
        <Input
          size={Input.Size.Medium}
          name="scheduleName"
          placeholder="Enter a schedule name"
          type="text"
          value={values.scheduleName}
          variant={errors.scheduleName ? Input.Variant.Error : undefined}
          onChange={onInputChange}
          onBlur={onInputBlur}
          disabled={!canEdit}
          autoFocus={mode === 'create'}
        />
        {errors.scheduleName && (
          <span className="regular-1 red-txt-6">{errors.scheduleName}</span>
        )}
      </div>

      <div className="bs-schedule-form__field">
        <label>Time zone</label>
        <div className="bs-schedule-form__timezone-select sequence-filter-menu">
          <Select<TimezoneOption>
            options={timezoneOptions}
            showSearch
            autoFocusSearch
            selectedOptionKey={values.timezone}
            optionFilterProp="key"
            filterOption={(value, option) =>
              option.value.toLowerCase().includes(value.toLowerCase())
            }
            optionRenderer={(option) => (
              <TimezoneOptionRenderer timezone={option.value} />
            )}
            showOptionsSeparator
            onSelect={onTimezoneSelect}
            selectedOptionRenderer={() => (
              <span>{getTimezoneValueByName(values.timezone)}</span>
            )}
            disabled={!canEdit}
            className="bs-schedule-form__timezone-select-input"
          />
        </div>
        {errors.timezone && (
          <span className="regular-1 red-txt-6">{errors.timezone}</span>
        )}
      </div>

      {slotErrorBanner && (
        <ScheduleValidationBanner message={slotErrorBanner} />
      )}

      <ul className="bs-schedule-form__days">
        {SCHEDULE_DAY_ROWS.map(({ label, day }) => {
          const dayTimeSlot = timeSlots.find((t) => t.day === day);
          const active = dayTimeSlot ? isDayActive(dayTimeSlot) : false;
          const slots = dayTimeSlot?.slots || [];
          const showApply = shouldShowApplyToActiveDays(
            timeSlots,
            day,
            lastFocusedDay,
          );

          const canAddSlot =
            slots.length < MAX_SLOTS_PER_DAY &&
            !slotErrorBanner &&
            !hasAnyIncompleteLastSlot;

          return (
            <li
              key={day}
              className={`bs-schedule-form__day-row${
                active ? '' : ' bs-schedule-form__day-row--inactive'
              }`}
            >
              <Checkbox
                checked={active}
                disabled={!canEdit}
                onChange={(checked) => onDayToggle(day, checked)}
                className="bs-schedule-form__day-checkbox"
              />
              <span className="bs-schedule-form__day-label">{label}</span>
              <div className="bs-schedule-form__day-content schedule-timeslot-container">
                {active &&
                  slots.map((slot, slotIndex) => (
                    <ScheduleTimeSlotPill
                      key={`${day}-${slotIndex + 1}`}
                      day={day}
                      slotIndex={slotIndex}
                      slot={slot}
                      daySlots={slots}
                      canEdit={canEdit}
                      onSlotChange={onSlotChange}
                      onSlotBlur={onInputSlotBlur}
                      onFieldFocus={setFocusedDay}
                      onRemove={removeSlot}
                      autoFocusStart={
                        focusNewSlot?.day === day &&
                        focusNewSlot?.slotIndex === slotIndex
                      }
                      onAutoFocusHandled={() => setFocusNewSlot(null)}
                    />
                  ))}
                <div>
                  {active && canEdit && (
                    <div className="bs-schedule-form__day-actions">
                      {slots.length < MAX_SLOTS_PER_DAY && (
                        <button
                          type="button"
                          className={`bs-schedule-form__add-slot${
                            !canAddSlot
                              ? ' bs-schedule-form__add-slot--disabled'
                              : ''
                          }`}
                          onClick={() => addSlotHandler(day)}
                          disabled={!canAddSlot}
                        >
                          <Plus
                            width={16}
                            height={16}
                            className="bs-schedule-form__add-slot-icon"
                          />
                          Add slot
                        </button>
                      )}
                      {showApply &&
                        !slotErrorBanner &&
                        !hasAnyIncompleteLastSlot && (
                          <OverlayTooltip
                            text="Apply to all Active days"
                            placement={Placement.Top}
                            className="bs-schedule-form__apply-tooltip"
                          >
                            <button
                              type="button"
                              className="bs-schedule-form__apply-btn"
                              aria-label="Apply to all Active days"
                              {...accessibleOnClick(() =>
                                onApplyToActiveDays(day),
                              )}
                            >
                              <Checks
                                width={16}
                                height={16}
                                className="bs-schedule-form__apply-btn-icon"
                              />
                            </button>
                          </OverlayTooltip>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {showInlineFooter && (
        <div className="schedule-footer bs-schedule-form__footer">
          {canEdit && (
            <Button
              type={Button.Type.Submit}
              variant={Button.Variant.Primary}
              isLoading={isSubmitLoading}
              disabled={isSubmitLoading || !isFormValid}
            >
              {submitLabel}
            </Button>
          )}
          {onDelete && schedule && hasPermission(Permissions.SCHEDULE_DELETE) && (
            <span
              className="delete-schedule semibold-1 pointer"
              {...accessibleOnClick(() => onDelete(schedule))}
            >
              <Icon identifier="trash" />
              Delete Schedule
            </span>
          )}
        </div>
      )}
    </form>
  );
};

export default ScheduleForm;
